/**
 * @file     DetailClientChrome.tsx
 * @readme   ./README.md
 * @purpose  Client island for /properties/[key] (MLS detail): fires mls_listing_viewed on mount, renders the Like/Liked CTA wired to /api/mls/saved via secureFetch (the local list plus the cards' sign-in prompt when signed out), and auto-likes on sign-in redirect (?action=save).
 */

'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { propertyCanonicalRoute, propertyMlsRoute } from '@/core/constants/routes';
import { ThumbsUpIcon } from '@/components/icons/ThumbsUpIcon';
import { propertyDetailContent as C } from './pageContent';
import { analyticsEvents } from '@/core/constants/analytics';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { readShareRef } from '@/services/analytics/attribution';
import { recordListingSignal } from '@/hooks/useListingSignals';
import { useWishlist } from '@/components/properties/useWishlist';
import { WatchedSnackbar } from '@/features/properties/components/PropertyCard/cardChrome';

type Props = {
  listingKey: string;
  city: string | null;
  listingSlug?: string;
  listPrice?: number | null;
  trackDetailView?: boolean;
  isAuthenticated: boolean;
  isSaved: boolean;
};

export function DetailClientChrome({ listingKey, city, listingSlug, listPrice, trackDetailView = false, isAuthenticated, isSaved }: Props) {
  const { t } = useTranslation();
  useListingDetailView({ listingKey, city, listingSlug, listPrice, enabled: trackDetailView });
  const { saved, pending, onSave, signInPromptOpen, closeSignInPrompt, signInHref } = useListingSave({
    listingKey,
    city,
    isAuthenticated,
    isSaved,
    returnPath: listingSlug ? propertyCanonicalRoute(listingSlug) : undefined,
  });

  return (
    <>
    <WatchedSnackbar open={signInPromptOpen} onClose={closeSignInPrompt} signInHref={signInHref} />
    <Button
      onClick={onSave}
      disabled={pending}
      variant={saved ? 'contained' : 'outlined'}
      size="medium"
      startIcon={<ThumbsUpIcon filled={saved} />}
      sx={{ textTransform: 'none', fontWeight: 500 }}
    >
      {saved
        ? t(C.save.ctaSaved.key, { defaultValue: C.save.ctaSaved.defaultValue })
        : t(C.save.cta.key, { defaultValue: C.save.cta.defaultValue })}
    </Button>
    </>
  );
}

/** Fires the slug page's view and close events once per mount. */
export function useListingDetailView({
  listingKey,
  city,
  listingSlug,
  listPrice,
  enabled = true,
}: {
  listingKey: string;
  city: string | null;
  listingSlug?: string;
  listPrice?: number | null;
  enabled?: boolean;
}) {
  const { trackEvent } = useAnalytics();
  const searchParams = useSearchParams();
  const pagePath = propertyMlsRoute(listingKey);

  useEffect(() => {
    if (!enabled) return;
    const openedAt = Date.now();
    const shareRef = readShareRef(searchParams);
    if (shareRef) {
      reltaTrack('share_opened', { listing_key: listingKey, ref: shareRef, listing_slug: listingSlug });
    }
    trackEvent({
      eventName: 'mls_listing_viewed',
      eventType: 'page',
      category: 'mls',
      label: 'properties_mls_detail',
      pagePath,
      metadata: { listingKey, city: city ?? undefined, source: 'property_detail_page' },
    });
    trackEvent({
      eventName: analyticsEvents.propertyDetailViewed,
      eventType: 'page',
      category: 'property_detail',
      label: 'slug_page',
      pagePath,
      metadata: {
        listing_key: listingKey,
        listing_slug: listingSlug ?? listingKey,
        list_price: listPrice ?? undefined,
        city: city ?? undefined,
        surface: 'slug_page',
      },
    });
    return () => {
      trackEvent({
        eventName: analyticsEvents.propertyDetailClosed,
        eventType: 'behavior',
        category: 'property_detail',
        label: 'slug_page',
        pagePath,
        metadata: {
          listing_key: listingKey,
          surface: 'slug_page',
          dwell_ms: Date.now() - openedAt,
        },
      });
    };
    // Fire once per mount. listingKey identity is stable for the page lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Saved state for one MLS listing, on the same list the cards use. Signed in,
 * it writes /api/mls/saved. Signed out, it saves to the local list and opens
 * the cards' sign-in prompt; that sign-in returns to the listing's canonical
 * slug path with ?action=save, which saves to the account on return.
 */
export function useListingSave({
  listingKey,
  city,
  isAuthenticated,
  isSaved,
  returnPath,
}: {
  listingKey: string;
  city: string | null;
  isAuthenticated: boolean;
  isSaved: boolean;
  /** The listing's canonical slug path. A key path 308s to the slug and drops the query. */
  returnPath?: string;
}) {
  const { trackEvent } = useAnalytics();
  const router = useRouter();
  const searchParams = useSearchParams();
  const wishlist = useWishlist();
  const [serverSaved, setServerSaved] = useState<boolean>(isSaved);
  const [pending, setPending] = useState<boolean>(false);
  const [signInPromptOpen, setSignInPromptOpen] = useState(false);
  const pagePath = propertyMlsRoute(listingKey);
  const backTo = returnPath ?? pagePath;
  const wishRef = { source: 'mls', listingKey } as const;
  const locallySaved = wishlist.has(wishRef);
  const saved = isAuthenticated ? serverSaved : locallySaved;
  const signInHref = `/sign-in?redirect=${encodeURIComponent(`${backTo}?action=save&key=${encodeURIComponent(listingKey)}`)}`;

  const doSave = async (target: 'save' | 'unsave'): Promise<boolean> => {
    setPending(true);
    try {
      const res = await secureFetch('/api/mls/saved', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: target, listingKey }),
      });
      if (!res.ok) return false;
      setServerSaved(target === 'save');
      if (target === 'save') wishlist.add(wishRef);
      else wishlist.remove(wishRef);
      trackEvent({
        eventName: target === 'save' ? 'mls_listing_saved' : 'mls_listing_unsaved',
        eventType: 'click',
        category: 'mls',
        label: 'properties_mls_detail_save',
        pagePath,
        metadata: { listingKey, city: city ?? undefined },
      });
      return true;
    } catch {
      return false;
    } finally {
      setPending(false);
    }
  };

  // Auto-save on sign-in redirect (?action=save&key=<x>)
  useEffect(() => {
    if (!isAuthenticated) return;
    const action = searchParams.get('action');
    const key = searchParams.get('key');
    if (action === 'save' && key === listingKey && !serverSaved) {
      void doSave('save').then(() => {
        router.replace(backTo);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const onSave = () => {
    trackEvent({
      eventName: 'mls_listing_save_clicked',
      eventType: 'click',
      category: 'mls',
      label: 'properties_mls_detail_save',
      pagePath,
      metadata: { listingKey, city: city ?? undefined },
    });
    if (!saved) recordListingSignal(listingKey, 'save');

    if (!isAuthenticated) {
      if (locallySaved) {
        wishlist.remove(wishRef);
        setSignInPromptOpen(false);
      } else {
        wishlist.add(wishRef);
        setSignInPromptOpen(true);
      }
      return;
    }

    void doSave(saved ? 'unsave' : 'save');
  };

  const closeSignInPrompt = useCallback(() => setSignInPromptOpen(false), []);

  return { saved, pending, onSave, signInPromptOpen, closeSignInPrompt, signInHref };
}
